<script setup lang="ts">
import type { ButtonProps } from '#ui/types';
import type { RouteLocationRaw } from '#vue-router';

import { ROUTE_NAMES } from '~/constants/routes';
import { isAuction, type PlatformSaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
}

const { sale } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();
const localePath = useLocalePath();
const { title } = useSaleTitle(() => sale);
const { frontendBaseUrl } = useRuntimeConfig().public;

// DATA
const source = computed(() => {
    const route = ((): RouteLocationRaw => {
        if (!sale.hiddenFromListing) return { name: ROUTE_NAMES.SMART_LINK_VEHICLE, params: { vin: sale.mainAsset.vin } };

        return { name: isAuction(sale) ? ROUTE_NAMES.AUCTION_DETAIL : ROUTE_NAMES.DIRECT_SALE_DETAIL, params: { slug: sale.slug } };
    })();

    return `${frontendBaseUrl}${localePath(route)}`;
});

const shareSource = computed(() => ({
    title: title.value,
    url: source.value,
}));

const buttonProps: Pick<ButtonProps, 'color' | 'variant'> = { color: 'primary', variant: 'soft' };

const { share, isSupported: isSupportedShare } = useShare(shareSource);
const { copy, copied, isSupported: isSupportedClipboard } = useClipboard({ source });

// HANDLERS
const onClickShare = (): Promise<void> => share();

const onClickClipboard = (): Promise<void> => copy(source.value);
</script>

<template>
    <UButton v-if="isSupportedShare" icon="lucide:share" :label="tt('sdp.share.label')" v-bind="buttonProps" @click="onClickShare" />
    <UButton v-else :disabled="!isSupportedClipboard" icon="lucide:copy" v-bind="buttonProps" @click="onClickClipboard">
        <span v-if="!copied">{{ tt('sdp.share.label') }}</span>
        <span v-else>{{ tt('sdp.share.copied') }}</span>
    </UButton>
</template>
